// Story 5.2 — TanStack Query mutation hook for POST
// /api/v1/marketplace/listings/share.
//
// Mirrors Story 4.5 (useChangeHandle) + Story 4.8 (useFlagContent):
//   * Generates an Idempotency-Key per mutation call (Story 1.6
//     contract; financial-mutation endpoints require it).
//   * On non-OK: parses the typed error envelope so the dialog can
//     branch on `code` (KYC_REQUIRED → in-dialog alert;
//     LISTING_ALREADY_EXISTS → toast + close).
//   * NO onSuccess cache invalidation in the hook itself — the
//     consumer (the Dialog) owns invalidation because it knows the
//     share_id to invalidate.
//
// Typed errors the consumer cares about:
//   * 400 VALIDATION_FAILED + field=price_cents | expires_in_days
//   * 402 KYC_REQUIRED + threshold_cents + current_cumulative_cents
//     + attempted_price_cents (Story 5.8 will upgrade to redirect)
//   * 409 LISTING_ALREADY_EXISTS — race / double-click
//   * 401 — stale session; SPA should re-auth

import { useMutation, type UseMutationResult } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export type ShareListingExpiresInDays = 7 | 30 | 90 | null;

export interface CreateShareListingVariables {
  share_id: string;
  price_cents: number;
  expires_in_days: ShareListingExpiresInDays;
  seller_anonymous: boolean;
  /** Seller allows buyers to offer below the asking price. Optional on the
   *  wire (backend defaults false); the listing dialog always sets it. */
  allow_bidding?: boolean;
}

export interface CreateShareListingFeeBreakdown {
  platform_fee_cents: number;
  creator_royalty_cents: number;
  estimated_proceeds_cents: number;
}

export interface CreateShareListingResponseData {
  listing_id: string;
  share_id: string;
  price_cents: number;
  status: string;
  seller_anonymous: boolean;
  listed_at: string;
  expires_at: string | null;
  fee_breakdown: CreateShareListingFeeBreakdown;
}

export interface CreateShareListingError {
  code: string;
  message: string;
  field?: string;
  threshold_cents?: number;
  current_cumulative_cents?: number;
  attempted_price_cents?: number;
  status: number;
}

function freshIdempotencyKey(): string {
  // crypto.randomUUID is available in vitest+jsdom (Node 19+) and in
  // every modern browser. Fall back to a poor-man's id if missing.
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

async function postCreateShareListing(
  variables: CreateShareListingVariables,
): Promise<CreateShareListingResponseData> {
  const response = await fetch("/api/v1/marketplace/listings/share", {
    method: "POST",
    credentials: "include",
    headers: {
      "content-type": "application/json",
      "x-idempotency-key": freshIdempotencyKey(),
    },
    body: JSON.stringify(variables),
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let detail: CreateShareListingError = {
      code: "CREATE_LISTING_FAILED",
      message: `Listing creation failed (HTTP ${response.status})`,
      status: response.status,
    };
    try {
      const raw = await response.json();
      if (raw?.error) {
        detail = {
          code: raw.error.code,
          message: raw.error.message,
          field: raw.error.field,
          threshold_cents: raw.error.threshold_cents,
          current_cumulative_cents: raw.error.current_cumulative_cents,
          attempted_price_cents: raw.error.attempted_price_cents,
          status: response.status,
        };
      }
    } catch {
      // Body not JSON — keep default.
    }
    throw detail;
  }
  const body = (await response.json()) as { data: CreateShareListingResponseData };
  return body.data;
}

export function useCreateShareListing(): UseMutationResult<
  CreateShareListingResponseData,
  CreateShareListingError,
  CreateShareListingVariables
> {
  return useMutation<
    CreateShareListingResponseData,
    CreateShareListingError,
    CreateShareListingVariables
  >({
    mutationFn: postCreateShareListing,
    // NO onSuccess — the Dialog (consumer) invalidates the share-detail
    // query and any other share-id-keyed caches because it knows which
    // share was just listed.
  });
}
